import {groupOpacityStyle} from '@waka/compat/group-opacity';
import React, { useMemo } from 'react';
import { Sequence, useCurrentFrame, useVideoConfig, useRemotionEnvironment } from 'remotion';
import { AudioLayer } from './audio/audio-layer';
import { BlurLayer } from './blur/blur-layer';
import { CaptionsLayer } from './captions/captions-layer';
import { CodeLayer } from './code/code-layer';
import {isWordChantCodeItem} from './code/code-item-type';
import { PdfLayer } from './pdf/pdf-layer';
import { GifLayer } from './gif/gif-layer';
import { ImageLayer } from './image/image-layer';
import { EditorStarterItem } from './item-type';
import { SolidLayer } from './solid/solid-layer';
import { TextLayer } from './text/text-layer';
import { VideoLayer } from './video/video-layer';
import { VideoAudioOnlyLayer } from './video/video-audio-only-layer';
import { WebpLayer } from './webp/webp-layer';
import { MaskWrapper } from '@/features/remotion-editor/editor/canvas/mask-wrapper';
import { calculateAnimationsStyle } from './animation/calculate-animation';
import { resolveGroupGeometry } from './group/group-geometry';
import { GroupItem } from './group/group-item-type';
import { toGroupRelativeItem } from '../canvas/utils/group-units';
import { useAllItems } from '../utils/use-context';
import type { CaptionPresentationRenderData } from './captions/caption-presentation-merge';

export const InnerLayer: React.FC<{
  item: EditorStarterItem;
  trackMuted: boolean;
  cropBackground: boolean;
  zIndex?: number;
  audioOnly?: boolean;
  disableBoundaryMediaPause?: boolean;
  captionPresentationData?: CaptionPresentationRenderData;
  groupParentBounds?: {left:number;top:number};
  groupAncestors?: readonly string[];
}> = ({
  item,
  trackMuted,
  cropBackground,
  zIndex,
  audioOnly,
  disableBoundaryMediaPause,
  captionPresentationData,
  groupParentBounds,
  groupAncestors,
}) => {
  const shouldMask =
    (item.type === 'image' || item.type === 'video') && item.isMainTrack;

  const renderContent = () => {
    if (item.type === 'group') return <GroupItemLayer group={item} zIndex={zIndex} trackMuted={trackMuted}
      audioOnly={audioOnly} disableBoundaryMediaPause={disableBoundaryMediaPause} parentBounds={groupParentBounds} ancestors={groupAncestors}/>;
    // audioOnly 모드: 오디오가 있는 타입만 렌더, 나머지는 스킵
    if (audioOnly) {
      if (item.type === 'code' && isWordChantCodeItem(item)) {
        return <CodeLayer item={item} trackMuted={trackMuted} audioOnly />;
      }
      if (item.type === 'video') {
        return (
          <VideoAudioOnlyLayer
            item={item}
            trackMuted={trackMuted}
            disableBoundaryMediaPause={disableBoundaryMediaPause}
          />
        );
      }

      if (item.type === 'audio') {
        return (
          <AudioLayer
            item={item}
            trackMuted={trackMuted}
            disableBoundaryMediaPause={disableBoundaryMediaPause}
          />
        );
      }

      // 시각적 요소만 있는 타입은 오디오가 없으므로 스킵
      return null;
    }

    if (item.type === 'image') {
      return (
        <ImageLayer
          item={item}
          cropBackground={cropBackground}
          zIndex={zIndex}
          disableBoundaryMediaPause={disableBoundaryMediaPause}
        />
      );
    }

    if (item.type === 'text') {
      return (
        <TextLayer
          item={item}
          zIndex={zIndex}
        />
      );
    }

    if (item.type === 'video') {
      return (
        <VideoLayer
          item={item}
          trackMuted={trackMuted}
          cropBackground={cropBackground}
          zIndex={zIndex}
          disableBoundaryMediaPause={disableBoundaryMediaPause}
        />
      );
    }

    if (item.type === 'solid') {
      return (
        <SolidLayer
          item={item}
          zIndex={zIndex}
        />
      );
    }

    if (item.type === 'blur') {
      return (
        <BlurLayer
          item={item}
          zIndex={zIndex}
        />
      );
    }

    if (item.type === 'audio') {
      return (
        <AudioLayer
          item={item}
          trackMuted={trackMuted}
          disableBoundaryMediaPause={disableBoundaryMediaPause}
        />
      );
    }

    if (item.type === 'captions') {
      return (
        <CaptionsLayer
          item={item}
          zIndex={zIndex}
          presentationData={captionPresentationData}
        />
      );
    }

    if (item.type === 'gif') {
      return (
        <GifLayer
          item={item}
          cropBackground={cropBackground}
          zIndex={zIndex}
        />
      );
    }

    if (item.type === 'webp') {
      return (
        <WebpLayer
          item={item}
          cropBackground={cropBackground}
          zIndex={zIndex}
        />
      );
    }

    if (item.type === 'code') {
      return (
        <CodeLayer
          item={item}
          trackMuted={trackMuted}
          zIndex={zIndex}
        />
      );
    }

    if (item.type === 'pdf' || item.type === 'ppt') {
      return (
        <PdfLayer
          item={item}
          cropBackground={cropBackground}
          zIndex={zIndex}
        />
      );
    }

    throw new Error(
      'Unimplemented item type: ' + JSON.stringify(item satisfies never),
    );
  };

  const content = renderContent();

  if (!content || !shouldMask) {
    return content;
  }

  return <MaskWrapper shouldApply>{content}</MaskWrapper>;
};

/**
 * GroupItem 렌더 — children 을 union bbox 컨테이너 안에서 z-order(배경→전경)로 렌더한다.
 * - 컨테이너 위치/크기 = resolveGroupGeometry(children union).
 * - 그룹 등장 애니메이션(group.animations)을 컨테이너에 1회 적용.
 * - 멤버는 bbox 상대좌표로 배치하되 개별 animations 는 유지한다(keepAnimations) — 그룹 애니는
 *   group.animations 라는 별도 필드라 이중 적용이 없다. 컨테이너 변형 + 멤버 변형이 CSS 로
 *   합성돼 "카드 전체 등장 + 멤버 개별 강조(pulse/move)" 가 동시에 가능하다.
 * - 멤버는 자기 [from, from+duration) Sequence 로 감싼다 — 그래야 멤버 layer 가 보는
 *   currentFrame/durationInFrames 기준이 멤버 자신이 되어 in/out/combo 타이밍이 맞고,
 *   그룹 내부 stagger(멤버별 from) 도 살아난다. deck bake 는 한 그룹 멤버에 같은 from/duration 을
 *   주므로(applyDeckEntranceAnimations) 기존 결과의 등장 시점은 그대로다.
 *
 * 렌더 변화 주의(2026-07-14): keepAnimations 로 멤버 애니가 처음으로 실제 렌더된다. 그룹 멤버가
 * 되는 배경 박스(boxSelector ⊆ groupSelector 인 cards/compare/chart/chat 등)에 프리셋
 * boxEntrance(bake-flow.ts)가 붙어 있었다면 지금까지 조용히 무시됐고 이제 재생된다 — 의도된
 * 복구지만 해당 프리셋은 눈으로 확인할 것.
 *
 * groupId 기반 GroupLayer(canvas/group-layer.tsx)와 공존한다 — 거긴 "멤버 공유 animations 를
 * 그룹 애니로 승격" 관례라 멤버 animations 를 strip 해야 하고(toGroupRelativeItem 기본값),
 * GroupItem 은 groupId 미보유(partitionGroups 비대상)라 children 이 이중 렌더되지 않는다.
 */
const GroupItemLayer: React.FC<{ group: GroupItem; zIndex?: number;trackMuted:boolean;audioOnly?:boolean;disableBoundaryMediaPause?:boolean;parentBounds?:{left:number;top:number};ancestors?:readonly string[] }> = ({
  group,
  zIndex,
  trackMuted,audioOnly,disableBoundaryMediaPause,parentBounds,ancestors,
}) => {
  const { items } = useAllItems();
  const { fps } = useVideoConfig();
  const currentFrame = useCurrentFrame();
  const bbox = useMemo(
    () => resolveGroupGeometry(group, items),
    [group, items],
  );
  const path=useMemo(()=>[...(ancestors??[]),group.id],[ancestors,group.id]);
  // Keep valid nested groups; only ancestor cycles are omitted as a defensive runtime guard.
  const members = useMemo(
    () =>
      group.children
        .map((id) => {
          const item=items[id];
          if(!item||path.includes(id))return undefined;
          return item.type==='group'?{...item,...resolveGroupGeometry(item,items,new Set([...path,id]))}:item;
        })
        .filter(
          (m): m is EditorStarterItem => m !== undefined,
        ),
    [group.children, items,path],
  );
  const animationStyle = useMemo(() => {
    const hasAnimations = group.animations && group.animations.length > 0;
    const hasPositionKeyframes =
      group.positionKeyframes && group.positionKeyframes.length > 0;
    if (!hasAnimations && !hasPositionKeyframes) return {};
    return calculateAnimationsStyle({
      animations: group.animations ?? [],
      currentFrame,
      framesPerSecond: fps,
      totalDurationInFrames: Math.max(1, bbox.durationInFrames),
      positionKeyframes: group.positionKeyframes,
    });
  }, [
    group.animations,
    group.positionKeyframes,
    currentFrame,
    fps,
    bbox.durationInFrames,
  ]);
  const {isClientSideRendering}=useRemotionEnvironment();
  const relativeMembers = useMemo(
    () =>
      members.map((m) =>
        m.type==='group'?m:toGroupRelativeItem(m, bbox, { keepAnimations: true }),
      ),
    [members, bbox],
  );
  return (
    <div
      style={{
        position: 'absolute',
        left: bbox.left-(parentBounds?.left??0),
        top: bbox.top-(parentBounds?.top??0),
        width: bbox.width,
        height: bbox.height,
        transformOrigin: 'center',
        zIndex,
        transform: animationStyle.transform,
        ...groupOpacityStyle(group.opacity, animationStyle.opacity, isClientSideRendering && !audioOnly),
        clipPath: animationStyle.clipPath,
      }}
    >
      {relativeMembers.map((m) => (
        <Sequence
          key={m.id}
          // 그룹 Sequence 안이라 프레임이 그룹 시작 기준 → 멤버 from 을 그룹 기준으로 환산.
          from={m.from - bbox.from}
          durationInFrames={Math.max(1, m.durationInFrames)}
          // display:contents — 래퍼 박스를 만들지 않아 DOM 순서(z-order)와 좌표가 그대로 유지된다.
          style={groupMemberSequenceStyle}
        >
          <InnerLayer
            item={m}
            trackMuted={trackMuted}
            cropBackground={false}
            audioOnly={audioOnly}
            disableBoundaryMediaPause={disableBoundaryMediaPause}
            groupParentBounds={bbox}
            groupAncestors={path}
          />
        </Sequence>
      ))}
    </div>
  );
};

const groupMemberSequenceStyle: React.CSSProperties = { display: 'contents' };
